import { DownOutlined } from '@ant-design/icons';
import { Bar, BarConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import { GET_IDEA_TOPICS } from '@frontend/data-access';
import { Button, Card, Dropdown, Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

function PopularTopicsStats({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<{ name: string; count: number }[]>([]);
  const [filter, setFilter] = useState<string[]>([
    'DRAFT',
    'PUBLISHED',
    'HIDDEN',
    'REPORTED',
  ]);
  const [topicFilter, setTopicFilter] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<{ key: string; label: string }>({
    key: 'HL',
    label: t('highest_to_lowest'),
  });
  const [getIdeaTopics, { loading }] = useLazyQuery<{
    dashboardTopIdeaTopics: { name: string; count: number }[];
  }>(GET_IDEA_TOPICS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const sortedData = sortData(data.dashboardTopIdeaTopics, sortOption.key);
      setData(sortedData);
    },
  });

  const sortOptions = useMemo(
    () => [
      { key: 'HL', label: t('highest_to_lowest') },
      { key: 'LH', label: t('lowest_to_highest') },
    ],
    [t]
  );

  const filterOptions = [
    { label: t('drafted'), value: 'DRAFT' },
    { label: t('published'), value: 'PUBLISHED' },
    { label: t('hidden'), value: 'HIDDEN' },
    { label: t('reported'), value: 'REPORTED' },
  ];

  const sortData = (
    dataToSort: { name: string; count: number }[],
    sortKey: string
  ) => {
    return [...dataToSort].sort((a, b) => {
      if (sortKey === 'HL') {
        return b.count - a.count;
      } else {
        return a.count - b.count;
      }
    });
  };

  useEffect(() => {
    const sortedData = sortData(data, sortOption.key);
    setData(sortedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  useEffect(() => {
    getIdeaTopics({
      variables: {
        statuses: filter,
      },
    });
  }, [filter, getIdeaTopics]);

  const config: BarConfig = {
    data: data,
    xField: 'count',
    yField: 'name',
    color: '#3BA1FF',
    appendPadding: [0, 20, 0, 0],
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
        labelFormatter: (value) => {
          const str = value.toString();
          if (str.length > 20) {
            return str.slice(0, 20) + '...';
          }
          return str;
        },
      },
    },
    label: {
      text: 'count',
      position: 'right',
      textAlign: 'start',
      dx: 8,
      style: {
        fill: 'rgba(0, 0, 0, 0.65)',
        fontFamily: 'Lufga',
      },
    },
    interaction: {
      tooltip: {
        name: 'name',
        render: (event, { title, items }) => {
          return (
            <div>
              <div>{title}</div>
              <div>
                {t('count')}: {items[0].value}
              </div>
            </div>
          );
        },
      },
    },
  };

  const handleFilterChange = (value: string[]) => {
    setFilter(value);
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
      className={`${className}`}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center space-x-3 space-y-4">
          <div className="text-sm text-gray-900 font-medium text-nowrap">
            {t('ideas_by_topics')}
          </div>{' '}
          <div>
            <Dropdown
              menu={{
                items: sortOptions.map((item) => ({
                  ...item,
                  onClick: () => setSortOption(item),
                })),
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button shape="round">
                {sortOption.label}
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <div className="flex space-x-3">
          <Select
            onChange={handleFilterChange}
            options={filterOptions}
            value={filter}
            placeholder=""
            allowClear
            mode="multiple"
            style={{ width: '100%' }}
            maxTagCount="responsive"
            optionFilterProp="label"
          />
          <Select
            onChange={(value: string[]) => setTopicFilter(value)}
            options={data?.map((item) => ({
              label: item.name,
              value: item.name,
            }))}
            value={topicFilter}
            placeholder={t('filter_by_topic')}
            allowClear
            mode="multiple"
            style={{ width: '100%' }}
            maxTagCount="responsive"
            optionFilterProp="label"
          />
        </div>
        <Bar
          loading={loading}
          {...config}
          data={data?.filter(
            (item) =>
              topicFilter.length === 0 || topicFilter.includes(item?.name)
          )}
        />
      </div>
    </Card>
  );
}

export default PopularTopicsStats;
